//@ts-ignore
import cloneDeep from "clone-deep";
//@ts-ignore
import omitDeep from "omit-deep";
import { useLazyQuery, useMutation } from "@apollo/client";
import React, { useEffect, useReducer, useState } from "react";
import { useHistory, useParams } from "react-router";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Switch from "react-switch";
import { LoaderDots } from "../../../../middleware/loaders";
import { lang } from "../../../../middleware/lang";
import { Product } from "../../../../models/product_model";
import {
  CREATE_PRODUCT,
  GET_PRODUCT,
  UPDATE_PRODUCT,
  DELETE_PRODUCT,
  GET_ZONES,
} from "../queries";
import { ActionType } from "./productContext/actions";
import { ProductContext } from "./productContext/context";
import { productReducer } from "./productContext/reducer";
import { initialProductState } from "./productContext/state";
import { has_access } from "../../../../middleware/privileges";
import { DeleteModal } from "../../../modals/deletes";
import ProductImage from "./ProductImage";
import CategoriesSearch from "./CategoriesSearch";
import AddZone from "./AddZone";
import SizesSearch from "./SizesSearch";
import IngredientsSearch from "./IngredientsSearch";
import OptionsSearch from "./OptionsSearch";
import { InputOption } from "./productContext/state";
import useSettings from "../../../../hooks/settings";
import { useQuery } from "@apollo/client";
import { SET_PRODUCT_AVAILABILITY } from "../queries";
import { GETPRODUCTS } from "../../orders/queries";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import ProductInfoPlain from "./ProductInfoPlain";

type Params = {
  id?: string;
};

type Zone = {
  id: number;
  name: string;
};

const ProductPanel: React.FC = () => {
  const { id } = useParams<Params>();
  const isEdit = !!id;
  const [product, setProduct] = useState<Product>({} as Product);
  const [showDelete, setShowDelete] = useState(false);
  const [model_edited, setModelEdited] = useState(false);
  const [settings] = useSettings();

  const history = useHistory();
  const [
    createProduct,
    { loading: saving, error, called, data: createdProduct },
  ] = useMutation(CREATE_PRODUCT);
  const [updateProduct, { loading: updating }] = useMutation(UPDATE_PRODUCT);
  const [deleteProduct] = useMutation(DELETE_PRODUCT);
  const [getProduct, { data: product_db, loading }] = useLazyQuery(
    GET_PRODUCT,
    { fetchPolicy: "cache-and-network" }
  );
  const { data: zonesData, loading: zonesLoading } = useQuery(GET_ZONES);
  const [setAvailability] = useMutation(SET_PRODUCT_AVAILABILITY);
  const [getProducts, { data: productsDb }] = useLazyQuery(GETPRODUCTS, {
    fetchPolicy: "no-cache",
  });

  const [state, dispatch] = useReducer(productReducer, initialProductState);

  useEffect(() => {
    if (error) {
      toast.error(error.message);
    }
    if (called && !error && createdProduct) {
      toast.success(lang("Producto creado", "Product created"));
      history.push(`/products/edit/${createdProduct.createProduct.id}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, saving, called, createdProduct, history]);

  useEffect(() => {
    if (id) {
      getProduct({ variables: { id: +id } });
    }
  }, [id, getProduct]);

  useEffect(() => {
    if (product_db?.getProduct) {
      const p = product_db.getProduct;
      setProduct(p);
      dispatch({
        type: ActionType.LoadProduct,
        payload: {
          price: p.price,
          title: p.title,
          model: p.model,
          description: p.description,
          category: p.category,
          has_option: p.has_option,
          sizes: p.sizes,
          ingredients: p.ingredients,
          zone_id: p.zone_id,
          snoozed: p.snoozed,
          status: p.status,
          status_web: p.status_web,
          options: p.options,
        },
      });
      setModelEdited(true);
    }
  }, [product_db]);

  useEffect(() => {
    if (productsDb) {
      const categories = [...productsDb.getCategories].sort((a: any, b: any) =>
        a.name > b.name ? 1 : -1
      );
      localStorage.setItem(
        "menu_all",
        JSON.stringify({ products: [...productsDb.getProducts], categories })
      );
    }
  }, [productsDb]);

  // Auto-generate SKU from title
  useEffect(() => {
    if (!state.title) {
      dispatch({ type: ActionType.SetModel, payload: "" });
      return;
    }
    if (model_edited) return;
    const chunks = state.title.toLocaleUpperCase().split(" ");
    const modelChunks = [];
    if (chunks.length === 1) {
      modelChunks.push(chunks[0]);
    } else {
      modelChunks.push(chunks[0].slice(0, 3));
      if (chunks.length === 2) {
        modelChunks.push(chunks[1].slice(0, 3));
      } else {
        modelChunks.push(chunks[1].slice(0, 2));
        modelChunks.push(chunks[2].slice(0, 3));
      }
    }
    dispatch({
      type: ActionType.SetModel,
      payload: modelChunks.join("-"),
    });
  }, [state.title, model_edited]);

  if (loading) {
    return <LoaderDots fullSize />;
  }

  const validateRequiredFields = () => {
    const missing = [];
    if (!state.title?.trim()) missing.push(lang("Título", "Title"));
    if ((!state.price || state.price <= 0) && state.sizes.length === 0)
      missing.push(lang("Precio", "Price"));
    if (!state.model?.trim()) missing.push("SKU");
    if (!state.description?.trim())
      missing.push(lang("Descripción", "Description"));
    if (!state.category?.name?.trim())
      missing.push(lang("Tipo de producto", "Product type"));
    return missing;
  };

  const hasRequired = () => validateRequiredFields().length === 0;

  const onSubmit = async () => {
    const missing = validateRequiredFields();
    if (missing.length > 0) {
      toast.error(
        `${lang("Completa los campos requeridos", "Complete required fields")}: ${missing.join(", ")}`
      );
      return;
    }
    const cleanState = omitDeep(cloneDeep(state), "__typename");
    try {
      if (!id) {
        createProduct({ variables: { data: cleanState } });
      } else {
        await updateProduct({ variables: { data: { id, ...cleanState } } });
        toast.success(lang("Producto actualizado", "Product updated"));
      }
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteProduct({ variables: { product_id: id } });
      toast.success(lang("Producto eliminado", "Product deleted"));
      history.push("/products");
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const saveAvailability = (
    snoozed: boolean,
    status: boolean,
    status_web: boolean
  ) => {
    if (!id) return;
    setAvailability({
      variables: { data: { product_id: +id, snoozed, status, status_web } },
    });
    getProducts();
  };

  const canEdit = has_access(256);

  return (
    <ProductContext.Provider value={{ state, dispatch }}>
      {/* Header */}
      <p>
        <Link to="/products">
          <i className="fa fa-chevron-left" aria-hidden="true"></i>{" "}
          {lang("Productos", "Products")}
        </Link>
      </p>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h1 className="mb-0">
          {isEdit
            ? product.title || lang("Editar Producto", "Edit Product")
            : lang("Nuevo Producto", "New Product")}
        </h1>
        {isEdit && product.id && (
          <a
            href={`https://fooapp.mx/es/pages/product/9/${product.id}`}
            className="btn btn-outline-secondary btn-sm"
            target="_blank"
            rel="noreferrer"
          >
            <i className="fas fa-external-link-alt"></i>{" "}
            {lang("Ver Producto", "View Product")}
          </a>
        )}
      </div>

      {!canEdit ? (
        <ProductInfoPlain product={product} />
      ) : (
        <>
          <div className="row">
            {/* ─── Left column ─── */}
            <div className="col-lg-6">
              {/* Basic info */}
              <div className="material_card p-3 mb-3">
                <h4>{lang("Datos del producto", "Product details")}</h4>
                <div className="form-group">
                  <label>
                    {lang("Título", "Title")} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={state.title}
                    onChange={(e) =>
                      dispatch({ type: ActionType.SetTitle, payload: e.target.value })
                    }
                    placeholder={lang("Ej: Hamburguesa clásica", "E.g. Classic burger")}
                    autoComplete="off"
                  />
                  <small className="form-text text-muted">
                    {lang("Máximo 150 caracteres", "Max 150 characters")}
                  </small>
                </div>
                <div className="row">
                  <div className="col-6">
                    <div className="form-group">
                      <label
                        className="hover_pointer"
                        onClick={() => {
                          if (!isEdit) setModelEdited(false);
                        }}
                      >
                        SKU <span className="text-danger">*</span>
                        {!isEdit && (
                          <small className="ml-2 text-muted">
                            {model_edited
                              ? lang("Autogenerar", "Auto-generate")
                              : lang("Autogenerado", "Auto-generated")}
                          </small>
                        )}
                      </label>
                      <input
                        type="text"
                        className="form-control text-uppercase"
                        placeholder="PROD-01"
                        disabled={isEdit}
                        value={state.model}
                        onChange={(e) => {
                          setModelEdited(true);
                          dispatch({
                            type: ActionType.SetModel,
                            payload: e.target.value.toLocaleUpperCase().split(" ").join("-"),
                          });
                        }}
                        autoComplete="new-password"
                      />
                      {!isEdit && (
                        <small className="form-text text-danger">
                          {lang(
                            "No se puede editar después de guardar",
                            "Cannot be edited after saving"
                          )}
                        </small>
                      )}
                    </div>
                  </div>
                  <div className="col-6">
                    <CategoriesSearch />
                  </div>
                </div>
                <div className="form-group">
                  <label>
                    {lang("Descripción", "Description")} <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    value={state.description}
                    onChange={(e) =>
                      dispatch({
                        type: ActionType.SetDescription,
                        payload: e.target.value,
                      })
                    }
                    rows={3}
                    placeholder={lang(
                      "Describe tu producto para los clientes",
                      "Describe your product for customers"
                    )}
                  />
                </div>
              </div>

              {/* Pricing */}
              <div className="material_card p-3 mb-3">
                <h4>{lang("Precio y zona", "Pricing & zone")}</h4>
                <div className="row">
                  <div className="col-6">
                    <div className="form-group">
                      <label>
                        {lang("Precio", "Price")} <span className="text-danger">*</span>
                      </label>
                      {state.sizes.length > 0 ? (
                        <div className="alert alert-info py-2 mb-0" role="alert">
                          <small>
                            <i className="fas fa-info-circle"></i>{" "}
                            {lang("Definido por tamaños", "Defined by sizes")}
                          </small>
                        </div>
                      ) : (
                        <>
                          <div className="input-group">
                            <div className="input-group-prepend">
                              <span className="input-group-text">$</span>
                            </div>
                            <input
                              type="text"
                              className="form-control"
                              value={state.price}
                              onChange={(e) =>
                                dispatch({
                                  type: ActionType.SetPrice,
                                  payload: +e.target.value,
                                })
                              }
                            />
                          </div>
                          <small className="form-text text-muted">
                            {settings.printer_iva
                              ? lang("Incluir IVA", "Tax included")
                              : lang("Tus precios no incluyen IVA", "Your prices don't include tax")}
                          </small>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group">
                      <label>{lang("Zona", "Zone")}</label>
                      {!zonesLoading && zonesData?.getZones && (
                        <>
                          <select
                            className="custom-select"
                            value={state.zone_id}
                            onChange={(e) =>
                              dispatch({
                                type: ActionType.SetZone,
                                payload: +e.target.value,
                              })
                            }
                          >
                            <option value={0}>{lang("Sin zona", "No zone")}</option>
                            {zonesData.getZones.map((z: Zone) => (
                              <option key={z.id} value={z.id}>
                                {z.name}
                              </option>
                            ))}
                          </select>
                          <AddZone />
                        </>
                      )}
                      <small className="form-text text-muted">
                        {lang("Separa productos en la comanda", "Separate products in order ticket")}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Image (edit only) */}
              {isEdit && (
                <div className="material_card p-3 mb-3">
                  <h4>{lang("Imagen", "Image")}</h4>
                  <div className="product_imgs">
                    <ProductImage images={product.images} productId={+product.id} />
                  </div>
                </div>
              )}
            </div>

            {/* ─── Right column ─── */}
            <div className="col-lg-6">
              {/* Availability (edit only) */}
              {isEdit && (
                <div className="material_card p-3 mb-3">
                  <h4>{lang("Disponibilidad", "Availability")}</h4>
                  <div className="d-flex align-items-center mb-3">
                    <Switch
                      id="snoozed"
                      checked={!state.snoozed}
                      onChange={(checked) => {
                        dispatch({ type: ActionType.SetSnoozed, payload: !checked });
                        saveAvailability(!checked, state.status, state.status_web);
                      }}
                    />
                    <span className="ml-2">{lang("Disponible", "Available")}</span>
                  </div>
                  {has_access(256) && (
                    <>
                      <h5 className="text-muted">{lang("Canales de venta", "Sales channels")}</h5>
                      <div className="d-flex align-items-center mb-2">
                        <Switch
                          id="status_web"
                          checked={state.status_web}
                          onChange={(checked) => {
                            dispatch({ type: ActionType.SetStatusWeb, payload: checked });
                            saveAvailability(state.snoozed, state.status, checked);
                          }}
                        />
                        <span className="ml-2">{lang("Web y Móvil", "Web & Mobile")}</span>
                      </div>
                      <div className="d-flex align-items-center">
                        <Switch
                          id="status_pos"
                          checked={state.status}
                          onChange={(checked) => {
                            dispatch({ type: ActionType.SetStatus, payload: checked });
                            saveAvailability(state.snoozed, checked, state.status_web);
                          }}
                        />
                        <span className="ml-2">{lang("Punto de venta", "Point of sale")}</span>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Sizes */}
              <div className="material_card p-3 mb-3">
                <h4>{lang("Tamaños / Presentaciones", "Sizes / Presentations")}</h4>
                <small className="form-text text-muted mb-3">
                  {lang(
                    "Agrega tamaños si el producto tiene varias presentaciones con diferente precio.",
                    "Add sizes if the product has multiple presentations with different prices."
                  )}
                </small>
                <SizesSearch />
                {state.sizes.length > 0 && (
                  <table className="table table-sm mt-3">
                    <thead className="thead-light">
                      <tr>
                        <th>{lang("Tamaño", "Size")}</th>
                        <th>{lang("Precio", "Price")}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {state.sizes.map((size, idx) => (
                        <tr key={`size-${idx}`}>
                          <td>{size.name}</td>
                          <td>
                            <div className="input-group input-group-sm">
                              <div className="input-group-prepend">
                                <span className="input-group-text">$</span>
                              </div>
                              <input
                                type="text"
                                className="form-control"
                                value={size.price}
                                onChange={(e) =>
                                  dispatch({
                                    type: ActionType.SetSizePrice,
                                    payload: { price: +e.target.value, idx },
                                  })
                                }
                                placeholder="0.00"
                              />
                            </div>
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() =>
                                dispatch({
                                  type: ActionType.RemoveSize,
                                  payload: { idx },
                                })
                              }
                            >
                              <i className="fa fa-times" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Ingredients */}
              <div className="material_card p-3 mb-3">
                <h4>{lang("Ingredientes", "Ingredients")}</h4>
                <small className="form-text text-muted mb-3">
                  {lang(
                    "Ingredientes base de este producto. El cliente puede deseleccionar al ordenar.",
                    "Base ingredients. Customers can deselect when ordering."
                  )}
                </small>
                <IngredientsSearch />
                {state.ingredients.length > 0 && (
                  <div className="mt-2">
                    {state.ingredients.map((ingredient, idx) => (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-info mr-1 mt-1 text-capitalize"
                        onClick={() =>
                          dispatch({
                            type: ActionType.RemoveIngredient,
                            payload: ingredient,
                          })
                        }
                        key={`ingredient-${idx}`}
                      >
                        {ingredient.name}{" "}
                        <i className="fa fa-times ml-1"></i>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Options */}
              <div className="material_card p-3 mb-3">
                <h4>{lang("Opciones extra", "Extra options")}</h4>
                <small className="form-text text-muted mb-3">
                  {lang(
                    "Extras opcionales con costo adicional para el cliente.",
                    "Optional extras with additional cost for the customer."
                  )}
                </small>
                <OptionsSearch />
                {state.options.length > 0 && (
                  <>
                    <table className="table table-sm mt-3">
                      <thead className="thead-light">
                        <tr>
                          <th>{lang("Opción", "Option")}</th>
                          <th>{lang("+ Precio", "+ Price")}</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {state.options.map((option: InputOption, idx: number) => (
                          <tr key={`option-${idx}`}>
                            <td>{option.ingredient.name}</td>
                            <td>
                              <div className="input-group input-group-sm">
                                <div className="input-group-prepend">
                                  <span className="input-group-text">$</span>
                                </div>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={option.price_mod}
                                  onChange={(e) =>
                                    dispatch({
                                      type: ActionType.SetOptionPrice,
                                      payload: { price: +e.target.value, idx },
                                    })
                                  }
                                />
                              </div>
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() =>
                                  dispatch({
                                    type: ActionType.RemoveOption,
                                    payload: option,
                                  })
                                }
                              >
                                <i className="fa fa-times" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="mt-2">
                      <label>
                        {lang("Máximo a escoger", "Max to choose")}: <strong>{state.has_option}</strong>
                      </label>
                      <Slider
                        min={1}
                        max={state.options.length}
                        dots={true}
                        onChange={(value) =>
                          dispatch({ type: ActionType.SetHasOption, payload: value })
                        }
                        value={state.has_option}
                        step={1}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Actions bar */}
          {!hasRequired() && (
            <div className="alert alert-warning" role="alert">
              <i className="fas fa-exclamation-triangle"></i>{" "}
              {lang(
                "Completa todos los campos requeridos (marcados con *) para guardar.",
                "Complete all required fields (marked with *) to save."
              )}
            </div>
          )}
          <div className="d-flex justify-content-between mt-2 mb-4">
            {isEdit && (
              <button
                className="btn btn-outline-danger"
                disabled={saving || updating || loading}
                onClick={() => setShowDelete(true)}
                type="button"
              >
                <i className="fa fa-trash"></i>{" "}
                {lang("Eliminar", "Delete")}
              </button>
            )}
            <button
              disabled={saving || updating || loading || !hasRequired()}
              className="btn btn-primary ml-auto"
              type="submit"
              onClick={onSubmit}
            >
              <i className="far fa-save"></i>{" "}
              {saving || updating
                ? lang("Guardando...", "Saving...")
                : lang("Guardar", "Save")}
            </button>
          </div>
        </>
      )}

      <DeleteModal
        open={showDelete}
        title={lang(
          `¿Eliminar producto "${state.title}"?`,
          `Delete product "${state.title}"?`
        )}
        toggle={() => setShowDelete(false)}
        handleConfirm={handleDelete}
        confirm_txt={lang("Eliminar", "Delete")}
      >
        <p>
          {lang(
            "El producto se eliminará permanentemente",
            "The product will be permanently deleted"
          )}
        </p>
      </DeleteModal>
    </ProductContext.Provider>
  );
};

export default ProductPanel;
